import {
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import Button from "@shared/components/button/Button";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from "@shared/components/button/types";

import AngleDownLight from "@svg/angle-down-light";

interface Props {
  description: ReactNode;
  defaultOpen?: boolean;
}

const COLLAPSED_HEIGHT = 168;

const InstructorDescriptionLayout: FC<Props> = ({
  description,
  defaultOpen = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [isOpen, toggleOpen] = useReducer((prev) => !prev, defaultOpen);
  const [isExpandable, setIsExpandable] = useState(false);

  useEffect(() => {
    const element = contentRef.current;

    if (!element) return;

    setIsExpandable(element.scrollHeight > COLLAPSED_HEIGHT);
  }, [description]);

  const contentStyle = useMemo(
    () => ({
      maxHeight: isOpen
        ? `${contentRef.current?.scrollHeight || 0}px`
        : `${COLLAPSED_HEIGHT}px`,
    }),
    [isOpen]
  );

  const shouldShowGradient = isExpandable && !isOpen;

  return (
    <div className="w-full">
      <div
        ref={contentRef}
        style={contentStyle}
        className={classNames(
          "body-md text-fds-secondary mt-6 overflow-hidden transition-all duration-300",
          shouldShowGradient && "gradientColor"
        )}
      >
        {description}
      </div>

      {isExpandable && !isOpen && (
        <div className="flex items-center justify-center">
          <Button
            variant={BUTTON_VARIANT.TEXT}
            color={BUTTON_COLOR.PRIMARY}
            size={BUTTON_SIZE.SMALL}
            className="w-max"
            endIcon={<AngleDownLight className="h-4 w-4" />}
            onClick={toggleOpen}
          >
            بیشتر
          </Button>
        </div>
      )}
    </div>
  );
};

export default InstructorDescriptionLayout;
